import React from 'react';
import { Grid, Stack } from '@mui/material';
import { Form, Field } from 'mobx-react-form';
import { observer } from 'mobx-react-lite';
import { wrapForm } from 'components/shared/form/withForm';
import { plugins } from 'forms/utils/formPlugins';
import { formOptions } from 'forms/utils/formOptions';

import { SiteDetails } from './siteDetails';
import { PropertyDescription } from './propertyDescription';
import { PropertyHistory } from './propertyHistory';
import { PROPERTY_TYPES, SITE_AREA_VALUES } from './constants';
import { FormOptions } from 'components/shared/form';

const formConfig: FormOptions = {
  fields: [
    {
      name: 'property',
      fields: [
        {
          name: 'address',
          fields: [
            {
              name: 'street',
              bindings: 'TextField',
              value: '',
            },
            {
              name: 'neighborhood',
              bindings: 'TextField',
              value: '',
            },
            {
              name: 'city',
              bindings: 'TextField',
              value: '',
            },
            {
              name: 'county',
              bindings: 'TextField',
              value: '',
            },
            {
              name: 'zip',
              bindings: 'TextField',
              value: '',
            },
            {
              name: 'state',
              bindings: 'Select',
              value: '',
            },
          ],
        },
        {
          name: 'coords',
          fields: [
            {
              name: 'latitude',
              bindings: 'TextField',
              type: 'number',
              value: '',
            },
            {
              name: 'longitude',
              bindings: 'TextField',
              type: 'number',
              value: '',
            },
          ],
        },
        {
          name: 'siteArea',
          bindings: 'TextField',
          type: 'number',
          value: 0,
          hooks: {
            onChange(field: Field) {
              const form = field.state.form;
              const measure = form.$('property.siteAreaMeasure').value;
              const siteArea = Number(field.value) || 0;

              const altSiteArea = measure === SITE_AREA_VALUES.SF ? siteArea / 43560 : siteArea * 43560;

              form.$('property.altSiteArea').set(altSiteArea);
            },
          },
        },
        {
          name: 'siteAreaMeasure',
          bindings: 'Select',
          value: '',
          hooks: {
            onChange(field) {
              const form = field.state.form;
              const siteArea = Number(form.$('property.siteArea').value) || 0;
              const newMeasure = field.value;

              const newAltMeasure = newMeasure === SITE_AREA_VALUES.SF ? SITE_AREA_VALUES.ACRE : SITE_AREA_VALUES.SF;
              const newAltSiteArea = newMeasure === SITE_AREA_VALUES.SF ? siteArea / 43560 : siteArea * 43560;

              form.$('property.altSiteAreaMeasure').set(newAltMeasure);
              form.$('property.altSiteArea').set(newAltSiteArea);
            },
          },
        },
        {
          name: 'displayInAcresAndSF',
          type: 'boolean',
          bindings: 'Checkbox',
          value: false,
        },
        {
          name: 'altSiteArea',
          bindings: 'TextField',
          type: 'number',
          value: 0,
        },
        {
          name: 'altSiteAreaMeasure',
          bindings: 'Select',
          value: '',
        },
        {
          name: 'residentialUnits',
          bindings: 'TextField',
          type: 'number',
          value: 0,
        },
        {
          name: 'commercialUnits',
          bindings: 'TextField',
          type: 'number',
          value: 0,
        },
        {
          name: 'yearBuilt',
          bindings: 'TextField',
          type: 'number',
          value: '',
        },
        {
          name: 'isYearBuiltEstimate',
          type: 'boolean',
          bindings: 'Checkbox',
          value: false,
        },
        {
          name: 'yearRenovated',
          bindings: 'TextField',
          type: 'number',
          value: '',
        },
        {
          name: 'buildingName',
          bindings: 'TextField',
          value: '',
        },
        {
          name: 'buildingClass',
          bindings: 'TextField',
          value: '',
        },
        {
          name: 'buildingType',
          bindings: 'Select',
          value: '',
        },
        {
          name: 'propertyType',
          bindings: 'Select',
          value: '',
          hooks: {
            onChange(field) {
              const form = field.state.form;
              const propertyType = field.value;

              if (propertyType === PROPERTY_TYPES.COMMERCIAL) {
                form.$('property.residentialUnits').set(0);
              }

              if (propertyType === PROPERTY_TYPES.MULTI_FAMILY) {
                form.$('property.commercialUnits').set(0);
              }

              if (propertyType !== PROPERTY_TYPES.MIXED_USE) {
                form.$('property.propertySubtype').set('');
              }
            },
          },
        },
        {
          name: 'propertySubtype',
          bindings: 'Select',
          value: '',
        },
        {
          name: 'floors',
          bindings: 'TextField',
          type: 'number',
          value: '',
        },
        {
          name: 'basisForSFAnalysis',
          type: 'text',
          bindings: 'TextField',
          value: '',
        },
        {
          name: 'basisForSFAnalysisValue',
          bindings: 'TextField',
          type: 'number',
          value: '',
        },
        {
          name: 'grossBuildingArea',
          bindings: 'TextField',
          type: 'number',
          value: '',
        },
        {
          name: 'currentOwner',
          bindings: 'TextField',
          value: '',
        },

        {
          name: 'generalCondition',
          bindings: 'Select',
          value: '',
        },
      ],
    },
    {
      name: 'propertyHistory',
      fields: [
        {
          name: 'salesHistory',
          type: 'array',
          value: [],
        },
        {
          name: 'salesHistory[].uniqueSaleId',
          bindings: 'TextField',
          value: '',
        },
        {
          name: 'salesHistory[].date',
          type: 'date',
          bindings: 'TextField',
          value: '',
        },
        {
          name: 'salesHistory[].price',
          type: 'number',
          bindings: 'TextField',
          value: '',
        },
        {
          name: 'salesHistory[].seller',
          bindings: 'TextField',
          value: '',
        },
        {
          name: 'salesHistory[].saleCondition',
          type: 'text',
          value: '',
        },
      ],
    },
  ],
  options: {
    ...formOptions,
    validateOnChange: true,
  },
  plugins,
};

type SubjectPropertyProps = {
  form: Form;
};

const SubjectProperty = observer(({ form }: SubjectPropertyProps) => {
  return (
    <Grid container>
      <Stack spacing={2}>
        <SiteDetails form={form} />
        <PropertyDescription form={form} />
        <PropertyHistory form={form} />
      </Stack>
    </Grid>
  );
});

export default wrapForm({
  ...formConfig,
})(SubjectProperty);
